/**
 * Shared sticky footer for a stepper page.
 *
 * Extracted from ChartMindFooter's nav row, which is where the app's primary
 * "advance" action actually lives. Anything that puts a Continue button inline in
 * the content column instead is inconsistent by construction — that was
 * VisitStart's bug.
 *
 * Layout, unchanged from the original: sticky to the bottom on background.default,
 * Back at `size="small"` on the left, an optional centered status slot, then an
 * optional secondary action and the primary at `size="large" variant="contained"`.
 * The right column is sized to its content rather than an equal third, because
 * two buttons in a third of a zoomed viewport wrap their labels.
 */

import React from "react";
import PropTypes from "prop-types";
import { Box, Button } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useTranslation } from "react-i18next";

export default function StepperPageFooter({
  onBack = undefined,
  backLabel = undefined,
  backDisabled = false,
  primaryLabel = undefined,
  onPrimary = undefined,
  primaryDisabled = false,
  primaryLoading = false,
  primaryType = "button",
  primaryFormId = undefined,
  secondaryLabel = undefined,
  onSecondary = undefined,
  secondaryDisabled = false,
  secondaryLoading = false,
  status = null,
  "data-testid": dataTestId = undefined,
  primaryTestId = undefined,
}) {
  const { t } = useTranslation("home");
  return (
    <Box
      sx={{
        position: "sticky",
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: "background.default",
        px: 2,
        py: 1,
        zIndex: 10,
        flexShrink: 0,
      }}
      data-testid={dataTestId}
    >
      {/* Status moves above the nav row on xs, where it has no room beside it. */}
      {status ? (
        <Box
          sx={{
            display: { xs: "flex", sm: "none" },
            justifyContent: "center",
            alignItems: "center",
            gap: 0.5,
            mb: 0.5,
          }}
        >
          {status}
        </Box>
      ) : null}

      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-start" }}>
          {onBack ? (
            <Button size="small" onClick={onBack} disabled={backDisabled}>
              {backLabel ?? t("common.back")}
            </Button>
          ) : null}
        </Box>

        <Box
          sx={{
            flex: 1,
            display: { xs: "none", sm: "flex" },
            justifyContent: "center",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {status}
        </Box>

        <Box
          sx={{
            flex: "0 0 auto",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 1,
          }}
        >
          {secondaryLabel && onSecondary ? (
            <LoadingButton
              size="large"
              onClick={onSecondary}
              disabled={secondaryDisabled}
              loading={secondaryLoading}
              sx={{ whiteSpace: "nowrap" }}
            >
              {secondaryLabel}
            </LoadingButton>
          ) : null}
          {primaryLabel ? (
            <LoadingButton
              // Associating the button with a form it is not inside makes it that
              // form's default submit button, which is what restores implicit
              // submission — pressing Enter in a field. Without this, hoisting the
              // button out of the form to the sticky footer silently breaks Enter.
              form={primaryFormId}
              type={primaryFormId ? "submit" : primaryType}
              size="large"
              variant="contained"
              loading={primaryLoading}
              onClick={onPrimary}
              disabled={primaryDisabled}
              sx={{ whiteSpace: "nowrap" }}
              data-testid={primaryTestId}
            >
              {primaryLabel}
            </LoadingButton>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}

StepperPageFooter.propTypes = {
  onBack: PropTypes.func,
  backLabel: PropTypes.string,
  backDisabled: PropTypes.bool,
  primaryLabel: PropTypes.string,
  onPrimary: PropTypes.func,
  primaryDisabled: PropTypes.bool,
  primaryLoading: PropTypes.bool,
  /** "submit" when the footer sits inside a form element. */
  primaryType: PropTypes.oneOf(["button", "submit"]),
  /** id of a form elsewhere on the page. Makes the primary that form's submit
   *  button so Enter in one of its fields submits. Forces type="submit". */
  primaryFormId: PropTypes.string,
  secondaryLabel: PropTypes.string,
  onSecondary: PropTypes.func,
  secondaryDisabled: PropTypes.bool,
  secondaryLoading: PropTypes.bool,
  /** Centered status slot (autosave state, counts). Use variant="caption". */
  status: PropTypes.node,
  "data-testid": PropTypes.string,
  primaryTestId: PropTypes.string,
};
